/**
 * C-GTCC-VIEW-OVW1 — the risk formula of the Risk Radar as a pure function.
 *
 * The instruction defines it as "silent devices + alert density + data
 * freshness, weighted"; it is kept here, free of React, fetching and clocks, so
 * that it is testable on its own (`risk-score.test.ts`) and so that the view
 * never decides a risk level inline.
 *
 * Honesty (Owner P8/P9/P10): a missing input is never substituted. If any of
 * the three inputs is `null`, the tenant's band is `unmeasurable` and its score
 * is `null` — the view then prints "nicht messbar" instead of a number, and
 * such a tenant sorts to the end of the table rather than to the top or bottom
 * of a ranking it was never part of.
 */

/** The three bands the reference prints as a word next to the dot. */
export const RISK_BANDS = ["elevated", "watch", "low", "unmeasurable"] as const;
export type RiskBand = (typeof RISK_BANDS)[number];

/** One tenant's measured inputs, exactly as `ops/vehicle-flow` delivers them. */
export interface RiskInput {
  /** Fleet size of the tenant. `null` or `0` makes the silent share unmeasurable. */
  readonly vehicles: number | null;
  readonly silentOver24h: number | null;
  readonly alerts7d: number | null;
  /** 0…100, the share of fresh sources the API measured for this tenant. */
  readonly dataConfidencePercent: number | null;
}

export interface RiskScore {
  /** 0…100, one decimal of significance; `null` when an input was missing. */
  readonly score: number | null;
  readonly band: RiskBand;
  /** The three normalised components, `null` where the input was missing. */
  readonly silentShare: number | null;
  readonly alertDensity: number | null;
  readonly freshnessGap: number | null;
}

/**
 * Weights. Silent trackers dominate because they are what the Owner calls
 * first about; alert density and freshness modulate.
 */
export const RISK_WEIGHT_SILENT = 0.55;
export const RISK_WEIGHT_ALERTS = 0.25;
export const RISK_WEIGHT_FRESHNESS = 0.2;

/** Alerts per tenant and week at which the alert component saturates. */
export const RISK_ALERT_SATURATION = 5;

/** Band thresholds on the 0…100 score. */
export const RISK_BAND_ELEVATED = 25;
export const RISK_BAND_WATCH = 10;

/**
 * Share of silent trackers above which the STILL > 24 H cell is drawn warn.
 * A tenth of a fleet unreachable for a day is the point at which the reference
 * colours the cell.
 */
export const SILENT_WARN_SHARE = 0.1;

function clamp01(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

/** Pure: same inputs, same result; reads no clock and no module state. */
export function computeRiskScore(input: RiskInput): RiskScore {
  const { vehicles, silentOver24h, alerts7d, dataConfidencePercent } = input;

  const silentShare =
    vehicles === null || silentOver24h === null || vehicles <= 0
      ? null
      : clamp01(silentOver24h / vehicles);
  const alertDensity = alerts7d === null ? null : clamp01(alerts7d / RISK_ALERT_SATURATION);
  const freshnessGap =
    dataConfidencePercent === null ? null : clamp01((100 - dataConfidencePercent) / 100);

  if (silentShare === null || alertDensity === null || freshnessGap === null) {
    return { score: null, band: "unmeasurable", silentShare, alertDensity, freshnessGap };
  }

  const score =
    100 *
    (RISK_WEIGHT_SILENT * silentShare +
      RISK_WEIGHT_ALERTS * alertDensity +
      RISK_WEIGHT_FRESHNESS * freshnessGap);

  return { score, band: bandOfScore(score), silentShare, alertDensity, freshnessGap };
}

export function bandOfScore(score: number): Exclude<RiskBand, "unmeasurable"> {
  if (score >= RISK_BAND_ELEVATED) return "elevated";
  if (score >= RISK_BAND_WATCH) return "watch";
  return "low";
}

/** True when the tenant's silent share crosses the warn threshold. */
export function isSilentShareWarn(vehicles: number | null, silent: number | null): boolean {
  if (vehicles === null || silent === null || vehicles <= 0) return false;
  return silent / vehicles >= SILENT_WARN_SHARE;
}

/**
 * Comparator for "sorted by risk descending": higher score first, measured
 * tenants before unmeasurable ones, ties broken by name so the order is total
 * and the table is stable across renders.
 */
export function compareByRiskDesc(
  a: { readonly score: number | null; readonly name: string },
  b: { readonly score: number | null; readonly name: string },
): number {
  if (a.score === null && b.score === null) return a.name.localeCompare(b.name);
  if (a.score === null) return 1;
  if (b.score === null) return -1;
  if (a.score !== b.score) return b.score - a.score;
  return a.name.localeCompare(b.name);
}
